import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';
import { PDFDocument, degrees } from 'pdf-lib';

/**
 * POST /api/documents/rotate-pdf
 *
 * Body: { document_ids: string[], angle?: number }
 *
 * Rotates all pages of the given PDF documents by the specified angle (default 90°).
 * Replaces the original files in storage.
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  const supabaseAdmin = getSupabaseAdminClient();

  try {
    const { document_ids, angle = 90 } = req.body;

    if (!document_ids || !Array.isArray(document_ids) || document_ids.length === 0) {
      return res.status(400).json({
        success: false,
        error: 'Se requiere al menos un document_id.',
      });
    }

    const { data: docs, error: docsError } = await supabaseAdmin
      .from('documentos')
      .select('id, nombre, url_archivo, empresa_id')
      .in('id', document_ids);

    if (docsError || !docs || docs.length === 0) {
      return res.status(404).json({
        success: false,
        error: 'No se encontraron documentos.',
      });
    }

    const results: Array<{ id: string; nombre: string; success: boolean; error?: string }> = [];

    for (const doc of docs) {
      if (!doc.nombre.toLowerCase().endsWith('.pdf')) {
        results.push({ id: doc.id, nombre: doc.nombre, success: false, error: 'No es PDF' });
        continue;
      }

      const storagePath = doc.url_archivo.replace(
        /^https:\/\/[^/]+\/storage\/v1\/object\/public\/documentos\//,
        ''
      );

      if (!storagePath || storagePath === doc.url_archivo) {
        results.push({ id: doc.id, nombre: doc.nombre, success: false, error: 'Ruta no válida' });
        continue;
      }

      try {
        // Download
        const { data: fileData, error: fileError } = await supabaseAdmin.storage
          .from('documentos')
          .download(storagePath);

        if (fileError || !fileData) {
          results.push({ id: doc.id, nombre: doc.nombre, success: false, error: 'Error descargando' });
          continue;
        }

        const pdfBytes = new Uint8Array(await fileData.arrayBuffer());
        const pdfDoc = await PDFDocument.load(pdfBytes);

        // Rotate all pages
        const pages = pdfDoc.getPages();
        pages.forEach(page => {
          const currentRotation = page.getRotation().angle;
          page.setRotation(degrees(currentRotation + angle));
        });

        const rotatedBytes = await pdfDoc.save();

        // Re-upload (overwrite)
        const { error: uploadError } = await supabaseAdmin.storage
          .from('documentos')
          .upload(storagePath, Buffer.from(rotatedBytes), {
            contentType: 'application/pdf',
            upsert: true,
          });

        if (uploadError) {
          results.push({ id: doc.id, nombre: doc.nombre, success: false, error: uploadError.message });
          continue;
        }

        results.push({ id: doc.id, nombre: doc.nombre, success: true });
      } catch (err: any) {
        results.push({ id: doc.id, nombre: doc.nombre, success: false, error: err.message || 'Error desconocido' });
      }
    }

    const successful = results.filter(r => r.success).length;
    return res.status(200).json({
      success: true,
      rotated: successful,
      total: docs.length,
      angle,
      results,
    });
  } catch (error: any) {
    console.error('[rotate-pdf] Error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error al rotar los PDFs.',
    });
  }
}

export default withAuth(handler, ['admin', 'asesor']);
